import { UserFeatureFlag, UserProfileRole } from '@@types/api.types';
import { EditSectionControls } from '@components/shared/edit-section-controls.component';
import { SectionCard } from '@components/shared/section-card.component';
import { Badge } from '@components/ui/badge';
import { Checkbox } from '@components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';
import { hasArrayChanges } from '@utils/array.util';
import { sanitizeUserFeatureFlags } from '@utils/feature-flag.util';
import { LockIcon } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

type AdminUsersModalFeatureFlagsProps = {
  effectiveFeatureFlags: UserFeatureFlag[];
  featureFlags: UserFeatureFlag[];
  isBusy: boolean;
  onUpdateFeatureFlags: (flags: UserFeatureFlag[]) => void;
  userRole?: UserProfileRole;
};

const FEATURE_FLAG_LABELS: Record<UserFeatureFlag, { description: string; label: string }> = {
  [UserFeatureFlag.CYBER_ASSESSMENTS_TOOL]: {
    label: 'Cyber Assessments Tool',
    description: 'Access to Cyber Assessments',
  },
  [UserFeatureFlag.INSIGHTS_TOOL]: {
    label: 'Insights Tool',
    description: 'Access to Visual Insights',
  },
  [UserFeatureFlag.MEIA_STUDIO_V1]: {
    label: 'MEIA Studio V1',
    description: 'Access to MEIA Studio',
  },
  [UserFeatureFlag.MEIA_STUDIO_V2]: {
    label: 'MEIA Studio V2',
    description: 'Deprecated: legacy Studies access',
  },
  [UserFeatureFlag.ORG_SWITCHER]: {
    label: 'Organization Switcher',
    description: 'Ability to switch between multiple organizations',
  },
  [UserFeatureFlag.RESEARCH_TOOL]: {
    label: 'Research Tool',
    description: 'Access to the Research Agent',
  },
  [UserFeatureFlag.THREADS_TOOL]: {
    label: 'Threads Tool',
    description: 'Access to Threads',
  },
  [UserFeatureFlag.TRL_PREDICTION]: {
    label: 'TRL Projection',
    description: 'Access to TRL projection visualizations',
  },
};

export const AdminUsersModalFeatureFlags: React.FC<AdminUsersModalFeatureFlagsProps> = (props) => {
  const { effectiveFeatureFlags, featureFlags, isBusy, onUpdateFeatureFlags, userRole } = props;

  const sanitizedEffectiveFeatureFlags = useMemo(() => {
    return sanitizeUserFeatureFlags(effectiveFeatureFlags);
  }, [effectiveFeatureFlags]);

  const sanitizedFeatureFlags = useMemo(() => {
    return sanitizeUserFeatureFlags(featureFlags);
  }, [featureFlags]);

  const [localFlags, setLocalFlags] = useState<UserFeatureFlag[]>(sanitizedFeatureFlags);
  const [isEditing, setIsEditing] = useState(false);

  // Compute org-inherited flags (flags from org that user doesn't have personally)
  const orgFlags = useMemo(() => {
    return sanitizedEffectiveFeatureFlags.filter(
      (flag) =>
        flag !== UserFeatureFlag.ORG_SWITCHER && // ORG_SWITCHER is always personal
        !sanitizedFeatureFlags.includes(flag),
    );
  }, [sanitizedEffectiveFeatureFlags, sanitizedFeatureFlags]);

  // Compute available personal flags based on user role
  // Always show tool flags + TRL_PREDICTION to allow visual duplication with org flags
  const availablePersonalFlags = useMemo(() => {
    const flags: UserFeatureFlag[] = [
      UserFeatureFlag.CYBER_ASSESSMENTS_TOOL,
      UserFeatureFlag.INSIGHTS_TOOL,
      UserFeatureFlag.MEIA_STUDIO_V1,
      UserFeatureFlag.RESEARCH_TOOL,
      UserFeatureFlag.THREADS_TOOL,
      UserFeatureFlag.TRL_PREDICTION,
    ];

    // ORG_SWITCHER: only for INNOVATOR users
    if (userRole === UserProfileRole.INNOVATOR) {
      flags.push(UserFeatureFlag.ORG_SWITCHER);
    }

    return flags;
  }, [userRole]);

  // Personal flags that are active (only those that are available for this user)
  const personalFlags = useMemo(() => {
    return sanitizedFeatureFlags.filter((flag) => availablePersonalFlags.includes(flag));
  }, [availablePersonalFlags, sanitizedFeatureFlags]);

  const hasChanges = useMemo(() => {
    return hasArrayChanges({ next: localFlags, previous: sanitizedFeatureFlags });
  }, [localFlags, sanitizedFeatureFlags]);

  const handleToggleFlag = useCallback((flag: UserFeatureFlag) => {
    setLocalFlags((prev) => {
      if (prev.includes(flag)) {
        return prev.filter((f) => f !== flag);
      }
      return [...prev, flag];
    });
  }, []);

  const handleSave = useCallback(() => {
    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    onUpdateFeatureFlags(localFlags);
    setIsEditing(false);
  }, [hasChanges, localFlags, onUpdateFeatureFlags]);

  const handleCancel = useCallback(() => {
    setLocalFlags(sanitizedFeatureFlags);
    setIsEditing(false);
  }, [sanitizedFeatureFlags]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const hasOrgFlags = orgFlags.length > 0;
  const hasPersonalFlags = personalFlags.length > 0;
  const hasNoFlags = !hasOrgFlags && !hasPersonalFlags;

  return (
    <SectionCard>
      <EditSectionControls
        cancelAriaLabel="Cancel Feature Flags"
        editAriaLabel="Edit Feature Flags"
        isCancelDisabled={isBusy}
        isEditing={isEditing}
        isSaveDisabled={isBusy}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onSave={handleSave}
        saveAriaLabel="Save Feature Flags"
      />
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Feature Flags</h3>
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-3">
            {/* Org-inherited flags (read-only) */}
            {hasOrgFlags && (
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-500">From Organization (read-only)</span>
                <div className="flex flex-wrap gap-2">
                  {orgFlags.map((flag) => (
                    <TooltipProvider key={flag}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            className={cn('border-transparent bg-blue-50 text-blue-600 hover:bg-blue-50')}
                            size="sm"
                            variant="secondary"
                          >
                            <LockIcon className="size-3 mr-1" />
                            {FEATURE_FLAG_LABELS[flag]?.label || flag}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          <p>Inherited from organization. Edit at organization level.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}

            {/* Personal flags (editable) */}
            {availablePersonalFlags.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-500">Personal Flags</span>
                {availablePersonalFlags.map((flag) => {
                  const { description, label } = FEATURE_FLAG_LABELS[flag];
                  return (
                    <div
                      key={flag}
                      className="flex items-start gap-3 p-2 rounded hover:bg-gray-50"
                    >
                      <Checkbox
                        checked={localFlags.includes(flag)}
                        disabled={isBusy}
                        id={`flag-${flag}`}
                        onCheckedChange={() => handleToggleFlag(flag)}
                      />
                      <label
                        className="flex flex-col gap-0.5 cursor-pointer flex-1"
                        htmlFor={`flag-${flag}`}
                      >
                        <span className="text-sm font-medium text-gray-900">{label}</span>
                        <span className="text-xs text-gray-500">{description}</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="h-2" />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Org-inherited flags display */}
            {hasOrgFlags && (
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-gray-500">From Organization</span>
                <div className="flex flex-wrap gap-2">
                  {orgFlags.map((flag) => (
                    <TooltipProvider key={flag}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            className={cn('border-transparent bg-blue-50 text-blue-600 hover:bg-blue-50')}
                            size="sm"
                            variant="secondary"
                          >
                            <LockIcon className="size-3 mr-1" />
                            {FEATURE_FLAG_LABELS[flag]?.label || flag}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          <p>Inherited from organization</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}

            {/* Personal flags display */}
            {hasPersonalFlags && (
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-gray-500">Personal</span>
                <div className="flex flex-wrap gap-2">
                  {personalFlags.map((flag) => (
                    <Badge
                      key={flag}
                      className={cn('border-transparent bg-blue-100 text-blue-700 hover:bg-blue-100')}
                      size="sm"
                      variant="secondary"
                    >
                      {FEATURE_FLAG_LABELS[flag]?.label || flag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {hasNoFlags && <span className="text-sm text-gray-500">No feature flags enabled</span>}
          </div>
        )}
      </div>
    </SectionCard>
  );
};
